import { inputOverviewSnapshot } from '../../utils/workflowIoOverview.mjs'

const AVAILABILITY = new Set(['ready', 'low_confidence', 'unavailable', 'stale'])
const TERMINAL = new Set(['success', 'failed', 'canceled', 'not_started'])
const boolean = value => value === true || (typeof value === 'string' && value.toLowerCase() === 'true')

export function durationInputSignature(workflow) {
  const visit = item => {
    const flows = item?.rpa?.rpaFlowList || []
    return {
      inputs: inputOverviewSnapshot(flows).sort((a, b) => String(a.id).localeCompare(String(b.id))),
      children: flows.filter(block => block.clickedRunRpa).map(block => [String(block.id), visit(block.clickedRunRpa)]).sort(([a], [b]) => a.localeCompare(b)),
    }
  }
  return JSON.stringify(visit(workflow))
}

export function durationParentSaveSignature(previous, current) {
  if (!previous) return null
  // Saving the parent definition does not prove that independent child PUTs succeeded.
  return JSON.stringify({ ...JSON.parse(current), children: JSON.parse(previous).children })
}

export function buildDurationOptions(params = {}) {
  const result = {
    engine_version: params.engine_version === 'v4' ? 'v4' : 'v2',
    is_reuse: boolean(params.is_reuse), inspection: boolean(params.inspection),
  }
  if (result.engine_version === 'v4') result.intermediate_storage = params.intermediate_storage === 'debug' ? 'debug' : 'memory'
  if (params.activity_id != null && params.activity_id !== '') result.activity_id = String(params.activity_id)
  if (params.fixed_today) result.fixed_today = params.fixed_today
  return result
}

export function formatDuration(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return null
  if (ms === 0) return '0초'
  if (ms < 1000) return '1초 미만'
  const seconds = Math.ceil(ms / 1000), hours = Math.floor(seconds / 3600), minutes = Math.floor(seconds % 3600 / 60)
  if (hours) return `${hours}시간${minutes ? ` ${minutes}분` : ''}`
  return `${minutes ? `${minutes}분` : ''}${seconds % 60 ? `${minutes ? ' ' : ''}${seconds % 60}초` : ''}`
}

function normalizedStatus(status = '') {
  const value = String(status).toLowerCase().replaceAll(' ', '_')
  if (value === 'finished') return 'success'
  if (['initialize_fail', 'starting_error', 'engine_error'].includes(value)) return 'failed'
  return value
}

export function durationPresentation(state = {}, { runtime = false, status = '' } = {}) {
  const payload = state.payload || {}, actualStatus = normalizedStatus(status)
  const phase = TERMINAL.has(actualStatus) || actualStatus === 'cancel_requested' ? actualStatus : payload.phase
  const result = {
    title: runtime ? '예상 남은 시간' : '예상 소요 시간', value: '예상 시간 정보 부족',
    short: '예측 정보 부족', range: '', detail: '', numeric: false, reference: false, warning: false,
    note: runtime ? '실행 상황에 따라 남은 시간이 늘어날 수 있습니다.' : '입력 데이터와 실행 환경에 따라 실제 소요 시간이 달라질 수 있습니다.',
  }
  if (runtime && actualStatus && !TERMINAL.has(actualStatus) && (TERMINAL.has(payload.phase) || TERMINAL.has(normalizedStatus(payload.status)))) {
    return { ...result, value: '실행 결과 확인 중', short: '실행 결과 확인 중', note: '기존 실행 상태 조회에서 최종 결과를 확인하고 있습니다.' }
  }
  if (runtime) {
    const elapsed = formatDuration(payload.elapsed_ms)
    const blocks = Number.isInteger(payload.completed_blocks) && Number.isInteger(payload.total_blocks) && payload.total_blocks > 0
      ? `블록 ${payload.completed_blocks} / ${payload.total_blocks} 완료` : ''
    result.detail = [elapsed ? `경과 ${elapsed}` : '', blocks].filter(Boolean).join(' · ')
  }
  const special = {
    queued: ['실행 대기 중', '실행이 시작되면 남은 시간을 표시합니다.'],
    issued: ['실행 대기 중', '실행이 시작되면 남은 시간을 표시합니다.'],
    preparing: ['실행 준비 중', '실행이 시작되면 남은 시간을 표시합니다.'],
    cancel_requested: ['중단 처리 중', '실제 중단 여부는 실행 상태에서 확인해 주세요.'],
    stale: ['예측 갱신 지연', '최신 예측 정보를 기다리고 있습니다.'],
    overrun: ['예상보다 오래 걸리는 중', '예상 시간 초과는 실행 실패를 의미하지 않습니다.'],
    failed: ['실행 실패', '실행 기록에서 오류 내용을 확인해 주세요.'],
    canceled: ['실행 중단', '중단된 실행의 남은 시간은 표시하지 않습니다.'],
    not_started: ['실행되지 않음', '실행이 시작되지 않았습니다.'],
  }
  if (runtime && phase === 'success') {
    const elapsed = formatDuration(payload.elapsed_ms)
    return { ...result, title: '실제 소요 시간', value: elapsed ? `총 ${elapsed} 소요` : '실행 완료', short: '실행 완료', note: '실행이 완료되었습니다.' }
  }
  if (runtime && special[phase]) {
    const [value, note] = special[phase]
    return { ...result, value, short: value, note, warning: phase === 'overrun' }
  }
  if (state.waiting) return { ...result, value: '저장 후 예측 갱신', short: '저장 후 갱신', note: '저장되지 않은 변경이 있어 이전 예측을 숨겼습니다.' }
  if (state.error) return { ...result, value: '예측 정보를 불러오지 못했습니다', short: '예측 조회 지연', note: '예측 조회와 관계없이 기존 실행·중단 기능을 사용할 수 있습니다.' }
  if (state.loading && !state.payload) return { ...result, value: '예상 시간 확인 중', short: '예측 확인 중' }
  if (payload.availability === 'stale') return { ...result, value: '예측 갱신 지연', short: '예측 갱신 지연' }
  const ms = runtime ? payload.remaining_ms : payload.estimated_duration_ms
  const formatted = formatDuration(ms)
  if (!['ready', 'low_confidence'].includes(payload.availability) || !formatted) return result
  const range = runtime ? payload.remaining_range_ms : payload.duration_range_ms
  const lower = formatDuration(range?.lower), upper = formatDuration(range?.upper)
  result.reference = payload.confidence === 'low' || payload.availability === 'low_confidence'
  result.numeric = true
  result.value = `약 ${formatted}`
  result.short = runtime ? `약 ${formatted} 남음` : `예상 약 ${formatted}`
  if (lower && upper && range.lower <= range.upper) result.range = `참고 범위 ${lower}~${upper}`
  if (result.reference) result.note = `참고용 예측입니다. 표본이나 측정 정보가 부족해 오차가 클 수 있습니다. ${result.note}`
  return result
}

// One owner, one target, one in-flight request. Prediction never mutates execution state.
export function createDurationController({ http, onChange, config = () => ({}), setTimer = setTimeout, clearTimer = clearTimeout }) {
  let target = null, signature = null, generation = 0, timer = null, request = null, abort = null, failures = 0
  let state = { payload: null, loading: false, error: null, waiting: false }
  const emit = patch => { state = { ...state, ...patch }; onChange({ ...state }) }
  const clear = () => { if (timer !== null) clearTimer(timer); timer = null }
  const cancel = () => { generation += 1; clear(); abort?.abort(); abort = null; request = null }
  const schedule = (callback, delay) => { clear(); timer = setTimer(() => { timer = null; callback() }, delay) }
  const run = () => {
    if (request || !target) return request
    const epoch = generation, snapshot = target
    abort = new AbortController()
    const requestConfig = { ...config(), params: undefined, signal: abort.signal, timeout: 10000, preserveGlobalOverlay: true, suppressGlobalErrorToast: true, preserveLicenseErrorHandling: true }
    emit({ loading: true, error: null })
    const promise = Promise.resolve().then(async () => {
      let retry = snapshot.runtime
      try {
        const response = snapshot.runtime
          ? await http.get(`annotation/rpa-executions/${encodeURIComponent(snapshot.executionToken)}/duration/`, requestConfig)
          : await http.post(`annotation/rpa/${snapshot.rpaId}/duration-estimate/`, snapshot.options, requestConfig)
        if (epoch !== generation) return
        const data = response?.data
        if (!data || !AVAILABILITY.has(data.availability) || (snapshot.runtime && data.execution_token !== snapshot.executionToken)) throw new Error('Invalid duration response')
        failures = 0
        emit({ payload: data, loading: false, error: null })
        if (TERMINAL.has(normalizedStatus(data.status)) || TERMINAL.has(data.phase)) retry = false
      } catch (error) {
        if (epoch !== generation) return
        failures += 1
        const status = error.response?.status
        if ([400, 401, 403, 404, 429].includes(status)) retry = false
        emit({ payload: null, loading: false, error: status || 'unavailable' })
      } finally {
        if (epoch === generation) {
          request = null
          if (retry) schedule(run, Math.min(30000, 5000 * Math.max(1, failures)))
        }
      }
    })
    request = promise
    return promise
  }
  const update = (value, force = false) => {
    const next = { ...value, options: buildDurationOptions(value.options) }
    const key = JSON.stringify([next.runtime, next.rpaId, next.executionToken, next.options, next.revision, next.ownerKey, next.ready, normalizedStatus(next.status)])
    if (!force && key === signature) return
    cancel(); target = next; signature = key; failures = 0
    const waiting = !next.ready
    emit({ payload: null, loading: false, error: null, waiting })
    if (waiting || (next.runtime ? !next.executionToken || TERMINAL.has(normalizedStatus(next.status)) : !Number.isInteger(Number(next.rpaId)) || Number(next.rpaId) <= 0)) return
    if (next.runtime) run()
    else schedule(run, 500)
  }
  return {
    update,
    refresh() { if (!request && target) update(target, true) },
    stop() { cancel(); target = null; signature = null; emit({ payload: null, loading: false, error: null, waiting: false }) },
  }
}
